import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PULL_INBOXES, PULL_STATES, listPulls, listRepositories } from '@mkcore/dev/schema';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Mono, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr, cn } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { connectionsQuery } from '@/features/admin/connections-panel';
import { CiBadge } from '@/features/dev/ci-badge';
import { ConflictBadge } from '@/features/dev/conflict-badge';

const search = z.object({
  inbox: z.enum(PULL_INBOXES).optional(),
  state: z.enum(PULL_STATES).optional(),
  repositoryId: z.string().optional(),
  q: z.string().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/dev/pulls/')({ validateSearch: search, component: Pulls });

const reposQuery = queryOptions({ queryKey: ['dev', 'repo', 'list', 'enabled'], queryFn: () => call(listRepositories, { enabled: true }) });
const pullsQuery = (s: Search) =>
  infiniteQueryOptions({
    queryKey: ['dev', 'pull', 'list', s],
    queryFn: ({ pageParam }) =>
      call(listPulls, {
        ...(s.inbox ? { inbox: s.inbox } : { state: s.state ?? 'open' }),
        ...(s.repositoryId && { repositoryId: s.repositoryId }),
        ...(s.q && { q: s.q }),
        ...(pageParam && { cursor: pageParam }),
      }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

const stateTone = { open: 'success', merged: 'info', closed: 'neutral' } as const;

/**
 * Pull requests over all mirrored repositories (CO-0420) with the inbox (CO-0422): what waits for your review, your
 * own open pull requests, and open ones without an approval. "You" is your linked Forgejo account.
 */
function Pulls() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const repos = useQuery(reposQuery);
  const q = useInfiniteQuery(pullsQuery(s));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const conn = useQuery(connectionsQuery);
  const linked = conn.data?.items.some((c) => c.provider === 'forgejo' && c.status === 'active') ?? false;
  const tabs: { key: Search['inbox'] | 'all'; label: string }[] = [
    { key: 'review', label: t('pulls.inbox.review') },
    { key: 'mine', label: t('pulls.inbox.mine') },
    { key: 'unreviewed', label: t('pulls.inbox.unreviewed') },
    { key: 'all', label: t('pulls.inbox.all') },
  ];
  const active = s.inbox ?? 'all';
  return (
    <>
      <PageHeader eyebrow={t('nav.dev')} title={t('pulls.title')} description={t('pulls.description')} />
      <nav aria-label={t('pulls.inboxes')} className="mb-4 flex flex-wrap gap-1 border-b border-line">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            aria-current={active === tab.key ? 'page' : undefined}
            className={cn('-mb-px border-b-2 px-3 py-2 text-sm', active === tab.key ? 'border-gold font-bold text-fg-1' : 'border-transparent text-fg-2 hover:text-fg-1')}
            onClick={() => set({ inbox: tab.key === 'all' ? undefined : tab.key })}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      {s.inbox && s.inbox !== 'unreviewed' && conn.data && !linked && (
        <p className="mb-3 text-sm text-fg-3">
          {t('pulls.linkForInbox')}{' '}
          <Link to="/settings" className="underline">
            {t('nav.settings')}
          </Link>
        </p>
      )}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('issues.search')} className="w-64">
          {(p) => <Input {...p} type="search" defaultValue={s.q ?? ''} placeholder={t('issues.searchHint')} onKeyDown={(e) => e.key === 'Enter' && set({ q: (e.target as HTMLInputElement).value.trim() || undefined })} />}
        </Field>
        <Field label={t('issues.repository')} className="w-56">
          {(p) => (
            <NativeSelect {...p} value={s.repositoryId ?? ''} onChange={(e) => set({ repositoryId: e.target.value || undefined })}>
              <option value="">{t('common.all')}</option>
              {(repos.data?.items ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.fullName}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        {!s.inbox && (
          <Field label={t('common.status')} className="w-36">
            {(p) => (
              <NativeSelect {...p} value={s.state ?? 'open'} onChange={(e) => set({ state: e.target.value as Search['state'] })}>
                {PULL_STATES.map((st) => (
                  <option key={st} value={st}>
                    {t(`pulls.state.${st}`)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        )}
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={s.inbox ? t('pulls.emptyInbox') : t('pulls.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('pulls.pull')}</Th>
              <Th className="hidden md:table-cell">{t('pulls.reviews')}</Th>
              <Th>{t('pulls.ci')}</Th>
              <Th className="hidden md:table-cell">{t('common.updated')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((p) => (
              <Tr key={p.id}>
                <Td>
                  <div className="flex items-baseline gap-2">
                    <Badge tone={stateTone[p.state]}>{t(`pulls.state.${p.state}`)}</Badge>
                    {p.draft && <Badge tone="outline">{t('pulls.draft')}</Badge>}
                    <ConflictBadge mergeable={p.mergeable} />
                    <Link to="/dev/pulls/$id" params={{ id: p.id }} className="font-bold hover:underline">
                      {p.title}
                    </Link>
                  </div>
                  <div className="mt-0.5 text-xs text-fg-3">
                    <Mono className="text-[11px]">
                      {p.repository}#{p.number}
                    </Mono>
                    {p.author && ` · ${p.author}`}
                    {p.headRef && p.baseRef && (
                      <>
                        {' · '}
                        <Mono className="text-[11px]">
                          {p.headRef} → {p.baseRef}
                        </Mono>
                      </>
                    )}
                  </div>
                </Td>
                <Td className="hidden md:table-cell text-sm">
                  <div className="flex flex-wrap gap-1">
                    {p.approvals > 0 && <Badge tone="success">{t('pulls.approvals', { count: p.approvals })}</Badge>}
                    {p.changesRequested && <Badge tone="danger">{t('pulls.changesRequested')}</Badge>}
                    {p.requestedReviewers.length > 0 && <span className="text-xs text-fg-3">{t('pulls.waitsFor', { names: p.requestedReviewers.join(', ') })}</span>}
                  </div>
                </Td>
                <Td>
                  <CiBadge state={p.ciState} />
                </Td>
                <Td className="hidden md:table-cell whitespace-nowrap tabular text-fg-2">{formatDateTime(p.updatedAt)}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </>
  );
}
